import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Cloud, Trophy, Tv, ShoppingCart, Newspaper, Calendar, MessageCircle, BookOpen, Home, Users, UtensilsCrossed, Plane, Heart, Landmark, ChevronDown, Menu, X } from 'lucide-react';

const TopNavBar = () => {
  const [showHomepageTooltip, setShowHomepageTooltip] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const quickLinks = [
    { name: 'Noticias', icon: Newspaper, url: 'https://www.eluniversal.com.mx' },
    { name: 'Clima', icon: Cloud, url: 'https://www.weather.com/es-MX/tiempo/hoy/l/Mexico+City+Mexico' },
    { name: 'Fútbol', icon: Trophy, url: 'https://www.espn.com.mx/futbol/mexico/' },
    { name: 'TV', icon: Tv, url: 'https://www.televisa.com/programacion/' },
    { name: 'Compras', icon: ShoppingCart, url: 'https://www.mercadolibre.com.mx' },
    { name: 'Correo', icon: Mail, url: 'https://mail.google.com' },
    { name: 'Calendario', icon: Calendar, url: 'https://calendar.google.com' }
  ];

  const siteMenu = [
    { name: 'Inicio', to: '/home', icon: Home },
    { name: 'Blog', to: '/blog', icon: BookOpen },
    { name: 'Recetas', to: '/blog?category=gastronomia', icon: UtensilsCrossed },
    { name: 'Viajes', to: '/blog?category=viajes', icon: Plane },
    { name: 'Salud', to: '/blog?category=salud', icon: Heart },
    { name: 'Finanzas', to: '/blog?category=finanzas', icon: Landmark },
    { name: 'Nosotros', to: '/nosotros', icon: Users },
  ];

  const isActive = (to) => {
    if (to === '/home') return location.pathname === '/' || location.pathname === '/home';
    if (to.includes('?')) return location.pathname + location.search === to;
    return location.pathname === to;
  };

  const handleMakeHomepage = () => {
    setShowHomepageTooltip(true);
    setTimeout(() => setShowHomepageTooltip(false), 5000);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white z-50 shadow-sm">
        {/* Top row: Logo + External links + Actions */}
        <div className="border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/home" className="flex items-center space-x-2 group">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center overflow-hidden shadow-sm group-hover:shadow-md transition-shadow" style={{
                  background: 'linear-gradient(to right, #006847 0%, #006847 33%, #FFFFFF 33%, #FFFFFF 66%, #CE1126 66%, #CE1126 100%)'
                }}>
                </div>
                <span className="font-heading text-xl font-bold tracking-tight">
                  <span className="text-gray-800">Portal </span>
                  <span className="italic" style={{ color: '#006341' }}>Méxi</span>
                  <span className="italic" style={{ color: '#ce1126' }}>co</span>
                </span>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center space-x-0.5">
              {quickLinks.map((link, index) => {
                const Icon = link.icon;
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-md text-gray-500 hover:bg-[#006341]/5 hover:text-[#006341] transition-all duration-200 text-xs">
                    <Icon className="w-3.5 h-3.5" />
                    <span className="font-medium">{link.name}</span>
                  </a>
                );
              })}
            </div>

            <div className="flex items-center space-x-1.5">
              {/* Make Homepage Button */}
              <div className="relative">
                <button
                  onClick={handleMakeHomepage}
                  data-testid="hacer-inicio-btn"
                  className="hidden sm:flex items-center space-x-1.5 px-3 py-1.5 text-white rounded-md font-heading font-semibold text-xs uppercase tracking-wide transition-all duration-200"
                  style={{ backgroundColor: '#ce1126' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#a50e1f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ce1126'}
                >
                  <Home className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Hacer Inicio</span>
                </button>
                
                {showHomepageTooltip && (
                  <div className="absolute top-full right-0 mt-2 w-72 bg-gray-900 text-white text-sm rounded-lg shadow-xl p-4 z-50">
                    <div className="absolute -top-2 right-4 w-4 h-4 bg-gray-900 transform rotate-45"></div>
                    <p className="font-semibold mb-2">Hacer portalmexi.co tu página de inicio:</p>
                    <ul className="space-y-1 text-gray-300 text-xs">
                      <li><strong>Chrome:</strong> Configuración → Al abrir → Abrir una página específica</li>
                      <li><strong>Firefox:</strong> Configuración → Inicio → Página de inicio personalizada</li>
                      <li><strong>Edge:</strong> Configuración → Al iniciar → Abrir una página específica</li>
                      <li><strong>Safari:</strong> Preferencias → General → Página de inicio</li>
                    </ul>
                    <p className="mt-2 text-xs text-orange-300">Ingresa: portalmexi.co</p>
                  </div>
                )}
              </div>
              
              {/* Contact Button */}
              <Link
                to="/contacto"
                data-testid="contacto-nav-btn"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span className="text-xs font-medium hidden sm:inline">Contacto</span>
              </Link>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="lg:hidden flex items-center justify-center w-9 h-9 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        </div>

        {/* Bottom row: Internal site menu */}
        <div className="hidden lg:block bg-[#FAFAF7] border-b border-gray-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center space-x-1 h-10">
              {siteMenu.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.name}
                    to={item.to}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                      active
                        ? 'bg-[#006341] text-white'
                        : 'text-[#1C3A27] hover:bg-[#006341]/10'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {siteMenu.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.name}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-[#006341] text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              <div className="border-t border-gray-100 pt-2 mt-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold px-3 mb-1">Enlaces rápidos</p>
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-500 hover:text-[#006341] rounded-lg hover:bg-gray-50 transition-all"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{link.name}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default TopNavBar;